import React, { useEffect } from 'react';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { FormInputText } from 'components/Common/Forms/FormTextField';
import { FormInputDate } from 'components/Common/Forms/FormInputDate';
import { HeadingMuiTypography, GridWrapper } from 'components/Ui/styled';

const GeneralInfoTab = () => {
  const employeeDetails = useSelector((state) => state.employeeDetails);

  const {
    givenName,
    familyName,
    homeAddress,
    dateOfBirth,
    jobTitle,
    emergencyContactGivenName,
    emergencyContactFamilyName,
    emergencyContactAddress,
    emergencyContactPhoneNumber,
    emergencyContactRelation,
    email,
    assignedLocations,
    memberOfGroups,
    readWriteSupervisorOfGroups,
    readOnlySupervisorOfGroups,
    activeCompanyAssociations,
  } = employeeDetails || {};

  const assignedLocationNames = [
    ...new Set(assignedLocations?.map((item) => item.val)),
  ];
  const memberOfGroupNames = [...new Set(memberOfGroups?.map((item) => item))];
  const readWriteSupervisorOfGroupNames = [
    ...new Set(readWriteSupervisorOfGroups?.map((item) => item)),
  ];
  const readOnlySupervisorOfGroupNames = [
    ...new Set(readOnlySupervisorOfGroups?.map((item) => item)),
  ];

  const activeCompanyAssociationNames = Object.keys(
    activeCompanyAssociations || {}
  ).filter((key) => activeCompanyAssociations?.[key]);


  const defaultValue = {
    firstName: givenName,
    lastName: familyName,
    homeAddress: homeAddress,
    dateOfBirth: dateOfBirth,
    jobTitle: jobTitle,
    ecFirstName: emergencyContactGivenName,
    ecLastName: emergencyContactFamilyName,
    ecHomeAddress: emergencyContactAddress,
    ecPhoneNumber: emergencyContactPhoneNumber,
    ecRelation: emergencyContactRelation,
    ecEmailAddress: email,
    assignedLocations: assignedLocationNames.join(', '),
    memberOfGroups: memberOfGroupNames.join(', '),
    readWriteSupervisorOfGroups: readWriteSupervisorOfGroupNames.join(', '),
    readOnlySupervisorOfGroups: readOnlySupervisorOfGroupNames.join(', '),
    activeCompanyAssociations: activeCompanyAssociationNames.join(', '),
  };

  const useFunction = useForm({
    mode: 'onChange',
    defaultValues: defaultValue,
  });

  const { handleSubmit, reset, control, register, formState } = useFunction;
  const { errors } = formState;

  useEffect(() => {
    reset(defaultValue);
  }, [
    defaultValue.firstName,
    defaultValue.lastName,
    defaultValue.homeAddress,
    defaultValue.dateOfBirth,
    defaultValue.jobTitle,
    defaultValue.ecFirstName,
    defaultValue.ecLastName,
    defaultValue.ecHomeAddress,
    defaultValue.ecPhoneNumber,
    defaultValue.ecRelation,
    defaultValue.ecEmailAddress,
    defaultValue.assignedLocations,
    defaultValue.memberOfGroups,
    defaultValue.readWriteSupervisorOfGroups,
    defaultValue.readOnlySupervisorOfGroups,
    defaultValue.activeCompanyAssociations,
  ]);

  const onSubmit = (data) => {
    console.log('data', data);
  };

  return (
    <>
      <HeadingMuiTypography variant="h6">
        Personal Information
      </HeadingMuiTypography>
      <GridWrapper container spacing={2}>
        <Grid container item xs={12} md={6}>
          <Grid container item spacing={2}>
            <Grid item xs={12} md={6}>
              <FormInputText
                name="firstName"
                type="text"
                control={control}
                label="First Name"
                testId="PI_FirstName"
                readOnly
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormInputText
                name="lastName"
                type="text"
                control={control}
                label="Last Name"
                testId="PI_LastName"
                readOnly
              />
            </Grid>
          </Grid>
          <FormInputText
            name="homeAddress"
            type="text"
            control={control}
            label="Home Address"
            testId="PI_HomeAddress"
            readOnly
          />
        </Grid>
        <Grid container item xs={12} md={6}>
          <Grid container item spacing={2}>
            <Grid item xs={12} md={6}>
              <FormInputDate
                name="dateOfBirth"
                control={control}
                label="Date Of Birth"
                helperText={errors?.dateOfBirth?.message}
                errors={!!errors.dateOfBirth}
                value={dateOfBirth}
                readOnly
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormInputText
                name="jobTitle"
                type="text"
                control={control}
                label="Job Title"
                readOnly
              />
            </Grid>
          </Grid>
        </Grid>
      </GridWrapper>
      <HeadingMuiTypography variant="h6">
        Emergency Contacts
      </HeadingMuiTypography>
      <GridWrapper container spacing={2}>
        <Grid container item xs={12} md={6}>
          <Grid container item spacing={2}>
            <Grid item xs={12} md={6}>
              <FormInputText
                name="ecFirstName"
                type="text"
                control={control}
                label="First Name"
                testId="EC_FirstName"
                readOnly
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormInputText
                name="ecLastName"
                type="text"
                control={control}
                label="Last Name"
                testId="EC_LastName"
                readOnly
              />
            </Grid>
          </Grid>
          <FormInputText
            name="ecHomeAddress"
            type="text"
            control={control}
            label="Home Address"
            testId="EC_HomeAddress"
            readOnly
          />
        </Grid>
        <Grid container item xs={12} md={6}>
          <Grid container item spacing={2}>
            <Grid item xs={12} md={6}>
              <FormInputText
                name="ecPhoneNumber"
                type="text"
                control={control}
                label="Phone Number"
                testId="EC_PhoneNumber"
                readOnly
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormInputText
                name="ecRelation"
                type="text"
                control={control}
                label="Relation"
                readOnly
              />
            </Grid>
          </Grid>
          <FormInputText
            name="ecEmailAddress"
            type="text"
            control={control}
            label="Email Address"
            readOnly
          />
        </Grid>
      </GridWrapper>
      <HeadingMuiTypography variant="h6">Other Info</HeadingMuiTypography>
      <GridWrapper container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormInputText
            name="assignedLocations"
            type="text"
            control={control}
            multiline
            rows={2}
            label="Assigned Locations"
            readOnly
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormInputText
            name="memberOfGroups"
            type="text"
            control={control}
            multiline
            rows={2}
            label="Member Of Groups"
            readOnly
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormInputText
            name="readOnlySupervisorOfGroups"
            type="text"
            control={control}
            multiline
            rows={2}
            label="Read Only Supervisor Of Groups"
            readOnly
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormInputText
            name="readWriteSupervisorOfGroups"
            type="text"
            control={control}
            multiline
            rows={2}
            label="Read Write Supervisor Of Groups"
            readOnly
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormInputText
            name="activeCompanyAssociations"
            type="text"
            control={control}
            multiline
            rows={2}
            label="Active Company Associations"
            readOnly
          />
        </Grid>
      </GridWrapper>
    </>
  );
};

export default GeneralInfoTab;
